/**
 * Signature auto-fill — frontend contract.
 *
 * A SIGNATURE attribute is either signed by hand on the form (MANUAL — the
 * original behaviour, and what a block with no config at all means) or mirrors a
 * signature the appointment already carries (AUTO, read-only). The api resolves
 * an AUTO block on every read, stamping `autoSignature` and `signatureFillState`
 * onto the attribute; neither is ever persisted.
 *
 * These strings are the stored wire contract, authored api-side in
 * `package/src/form-builder/constants/signatureFill.constants.ts`. They are
 * re-declared here rather than imported, because `@therapylake/package` is a
 * backend build: the client's `prebuild` runs the package's `build:frontend`,
 * which emits only `dist/sentry-shared`, and dist directories are dockerignored.
 * Importing anything else from the package therefore resolves on a developer
 * machine that happens to have run the backend build and fails in the client
 * image build. Keep the values here in step with that file — they must not
 * diverge.
 */

/** How a signature block gets its content. Absence of config means MANUAL. */
export const signatureFillModeEnums = {
    MANUAL: 'MANUAL',
    AUTO: 'AUTO',
};

/** Which of the appointment's two signatures an AUTO block mirrors. */
export const signatureFillSourceEnums = {
    /** `appt.signature` — captured from the client or their guardian. */
    CLIENT_GUARDIAN: 'CLIENT_GUARDIAN',
    /** `appt.providerSignature` — captured from the rendering provider. */
    PROVIDER: 'PROVIDER',
};

export const signatureFillSourceNames = {
    [signatureFillSourceEnums.CLIENT_GUARDIAN]: 'Client/Guardian Signature',
    [signatureFillSourceEnums.PROVIDER]: 'Provider Signature',
};

/** Why an AUTO block has nothing to show. Stamped as `signatureFillState`. */
export const signatureFillStateEnums = {
    /** Resolved — `autoSignature` holds the mirrored file. */
    OK: 'OK',
    /** The appointment exists but carries no signature of that kind yet. */
    NOT_SIGNED: 'NOT_SIGNED',
    /** The form is not attached to an appointment, so there is nothing to mirror. */
    NO_APPOINTMENT: 'NO_APPOINTMENT',
};

const SIGNATURE_FILL_EMPTY_MESSAGES = {
    [signatureFillStateEnums.NOT_SIGNED]: 'Not signed yet.',
    [signatureFillStateEnums.NO_APPOINTMENT]: 'No appointment signature to show.',
};

/** Shown when a block carries no resolvable state at all. */
export const SIGNATURE_FILL_UNKNOWN_MESSAGE = 'No signature to show.';

/** Pickable sources for the properties panel, in catalog order. */
export const signatureFillSourceList = Object.values(signatureFillSourceEnums).map((id) => ({
    id,
    name: signatureFillSourceNames[id],
}));

export const getSignatureFillSourceName = (source) => signatureFillSourceNames[source] ?? '';

/**
 * An unknown or absent state must NOT borrow the NOT_SIGNED wording: a block the
 * api never stamped would then assert something false about the appointment.
 */
export const getSignatureFillEmptyMessage = (state) =>
    SIGNATURE_FILL_EMPTY_MESSAGES[state] ?? SIGNATURE_FILL_UNKNOWN_MESSAGE;

/**
 * True when a signature block mirrors the appointment instead of being signed by
 * hand. Read from `attribute.properties`; a block with no config is MANUAL.
 */
export const isAutoSignature = (properties) => properties?.signatureFill?.mode === signatureFillModeEnums.AUTO;
